import React, { FC, useMemo, useState } from 'react'
import {
    DonutChartInsight,
    LineFillInsight,
    LineGraphInsight,
    MultiBarGraphInsight,
    MultiCounterInsight,
    QuickStatInsight,
    SingleBarGraphInsight,
    TotalResilienceActionsInsight,
    colors,
} from '@hazadapt-git/public-core-base'
import { BarGraphInsightView } from './BarGraphInsightView'
import { LineGraphInsightView } from './LineGraphInsightView'
import { MapInsightView, MapInsightViewProps } from './MapInsightView'
import { primaryIconSize, useStyles } from '../../lib/styles/universal'
import { MenuItemProps } from '../../lib/entities'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { IoEllipsisVertical, IoInformationCircle } from 'react-icons/io5'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import classNames from 'classnames'
import { DonutChartInsightView } from './DonutChartInsightView'
import { LineFillInsightView } from './LineFillInsightView'
import { MultiCounterInsightView } from './MultiCounterInsightView'
import { QuickStatInsightView } from './QuickStatInsightView'
import { Popover } from '../atoms'
import { insightHasData, useWindowSizeUp } from '../../lib/utils'
import {
    CustomMenuItem,
    topAlignedPopoverPositioning,
} from '../../lib/entities/misc'
import { InsightDemographicModeTag } from './InsightDemographicModeTag'
import { makeStyles } from 'tss-react/mui'
import { LoadingOverlay } from '../atoms/LoadingOverlay'
import { useInlineUpdateInsight } from '../../lib/hooks/useInlineUpdateInsight'

export type InsightViewBaseProps =
    | Omit<SingleBarGraphInsight, 'headline'>
    | Omit<MultiBarGraphInsight, 'headline'>
    | Omit<LineGraphInsight, 'headline'>
    | Omit<MapInsightViewProps, 'headline'>
    | Omit<DonutChartInsight, 'headline'>
    | Omit<LineFillInsight, 'headline'>
    | Omit<MultiCounterInsight, 'headline'>
    | Omit<QuickStatInsight, 'headline'>
		| Omit<TotalResilienceActionsInsight, 'headline'>

export type InsightViewProps = InsightViewBaseProps & {
    placement_id: string
    headline: React.ReactNode
    handleCtaClick?(href: string): void
    options: MenuItemProps[]
    onOpenFullScreen?: () => void
}

export const InsightView: FC<InsightViewProps> = (props: InsightViewProps) => {
    const { classes } = useStyles()
    const { classes: localClasses } = useLocalStyles()
    const lgScreen = useWindowSizeUp('lg')
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const { loading } = useInlineUpdateInsight(
        props.headline,
        props.date_updated
    )

    const onOptionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const onOptionsClose = () => {
        setAnchorEl(null)
    }

    const hasData = useMemo(() => insightHasData(props), [props])

    return (
        <div
            className={classes.insightView}
            onDoubleClick={() => props.onOpenFullScreen?.()}
        >
            <div className={classes.insightWrapper} id={props.placement_id}>
                {props.type === 'map' ? (
                    <MapInsightView {...props} />
                ) : (
                    <div className={classes.insightTitleAndElement}>
                        {props.type !== 'quick-stat' ? (
                            <Box
                                mb="1rem"
                                display="flex"
                                flexDirection="column"
                                gap="1rem"
                            >
                                <div className={classes.insightHeader}>
                                    <div
                                        className={classes.insightTitle}
                                        style={{
                                            flex: 1,
                                            justifyContent:
                                                props.headline === 'Insights'
                                                    ? 'center'
                                                    : undefined,
                                        }}
                                    >
                                        <Typography
                                            display="flex"
                                            alignItems="center"
                                            component="p"
                                            fontWeight={500}
                                            fontSize="1.125rem"
                                            flexWrap="wrap"
                                        >
                                            {props.headline}
                                        </Typography>
                                        {props.explainer ? (
                                            <Popover
                                                {...topAlignedPopoverPositioning}
                                                icon={
                                                    <IoInformationCircle
                                                        color={
                                                            colors.grays.NIMBUS
                                                        }
                                                    />
                                                }
                                                helpText={props.explainer}
                                            />
                                        ) : null}
                                    </div>
                                    {hasData &&
                                        (props.options.length > 0 ||
                                            (lgScreen &&
                                                props.onOpenFullScreen)) && (
                                            <div
                                                className={
                                                    classes.insightActions
                                                }
                                            >
                                                <IconButton
                                                    onClick={onOptionsClick}
                                                >
                                                    <IoEllipsisVertical
                                                        size={primaryIconSize}
                                                        color={
                                                            colors.grays.NOIR
                                                        }
                                                        aria-label="Open insight options menu"
                                                    />
                                                </IconButton>
                                                <Menu
                                                    id="insight-menu"
                                                    anchorEl={anchorEl}
                                                    open={open}
                                                    onClose={onOptionsClose}
                                                >
                                                    {lgScreen &&
                                                        props.onOpenFullScreen && (
                                                            <CustomMenuItem
                                                                label="View Fullscreen"
                                                                icon={
                                                                    <FullscreenIcon />
                                                                }
                                                                onClick={() => {
                                                                    props.onOpenFullScreen?.()
                                                                    onOptionsClose()
                                                                }}
                                                            />
                                                        )}
                                                    {props.options.length > 0 &&
                                                        props.options.map(
                                                            (o) => (
                                                                <CustomMenuItem
                                                                    {...o}
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        o.onClickAsync?.()
                                                                        onOptionsClose()
                                                                    }}
                                                                    key={
                                                                        o.label
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                </Menu>
                                            </div>
                                        )}
                                </div>
                                <div className={classes.insightSubline}>
                                    {props.subline && (
                                        <Typography>{props.subline}</Typography>
                                    )}
                                    {props.demographic_mode && (
                                        <div
                                            className={
                                                localClasses.insightDemographicModeTagContainer
                                            }
                                        >
                                            <InsightDemographicModeTag
                                                mode={props.demographic_mode}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Box>
                        ) : null}
                        <Box
                            className={classes.insight}
                            height={
                                props.type === 'line-fill' ? '100%' : undefined
                            }
                        >
                            {loading ? (
                                <LoadingOverlay loading={loading} />
                            ) : null}
                            {hasData || props.show_if_no_data ? (
                                props.type === 'bar' ? (
                                    <BarGraphInsightView {...props} />
                                ) : props.type === 'line' ? (
                                    <LineGraphInsightView {...props} />
                                ) : props.type === 'donut' ? (
                                    <DonutChartInsightView {...props} />
                                ) : props.type === 'line-fill' ? (
                                    <LineFillInsightView {...props} />
                                ) : props.type === 'multi-counter' ? (
                                    <MultiCounterInsightView {...props} />
                                ) : props.type === 'quick-stat' ? (
                                    <QuickStatInsightView {...props} />
                                ) : null
                            ) : (
                                <Typography margin="auto">
                                    Not enough data
                                </Typography>
                            )}
                        </Box>
                        {props.ctas && props.ctas.length > 0 && (
                            <div
                                className={classNames(
                                    classes.buttonGroup,
                                    classes.insightCtas
                                )}
                            >
                                {props.ctas.map((cta, index) => (
                                    <Button
                                        key={`${index + 1}-${cta.label
                                            .toLowerCase()
                                            .replaceAll(' ', '-')}`}
                                        onClick={() =>
                                            props.handleCtaClick?.(cta.href)
                                        }
                                        variant="contained"
                                        className={classes.smallButton}
                                    >
                                        {cta.label}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    insightDemographicModeTagContainer: {
        marginLeft: 'auto',
        alignSelf: 'flex-end',
    },
})
